import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { SiteConfigProvider } from './contexts/SiteConfigContext';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import Layout from './components/Layout';
import BootScreen from './components/BootScreen';
import Home from './pages/Home';
import BooksPage from './pages/BooksPage';
import MembershipsPage from './pages/MembershipsPage';
import AdvisoryPage from './pages/AdvisoryPage';
import BlogsPage from './pages/BlogsPage';
import ToolsPage from './pages/ToolsPage';
import ToolDiagnose from './pages/ToolDiagnose';
import ToolAppeal from './pages/ToolAppeal';
import ToolSecurityScore from './pages/ToolSecurityScore';
import BreachChecker from './pages/BreachChecker';
import RecoveryOdds from './pages/RecoveryOdds';
import PhishingDetector from './pages/PhishingDetector';
import AccountWorth from './pages/AccountWorth';
import SelfieCoach from './pages/SelfieCoach';
import SecuritySuitePage from './pages/SecuritySuitePage';
import InfoPage from './pages/InfoPage';
import TeamPage from './pages/TeamPage';
import OrderTracker from './pages/OrderTracker';
import FAQPage from './pages/FAQPage';
import NotFound from './pages/NotFound';
import AdminPanel from './pages/AdminPanel';
import FontPreview from './pages/FontPreview';
import Login from './pages/Login';
import Signup from './pages/Signup';
import MyAccount from './pages/MyAccount';
import MyWallet from './pages/MyWallet';
import Receipt from './pages/Receipt';
import RefundTracker from './pages/RefundTracker';
import PaymentReturn from './pages/PaymentReturn';
import SpinWheel from './pages/SpinWheel';
import OrderDetail from './pages/OrderDetail';
import ReferralsPage from './pages/ReferralsPage';
import CartPage from './pages/CartPage';
import FeedPage from './pages/FeedPage';
import Subscribe from './pages/Subscribe';
import HackingInfraPage from './pages/HackingInfraPage';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import EthicsPage from './pages/EthicsPage';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';

const SiteShell = ({ children }) => <Layout>{children}</Layout>;

// Safe sessionStorage helpers (iOS Safari private mode silently throws)
const safeGetSession = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const safeSetSession = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* noop */ } };

const BootGate = () => {
  const location = useLocation();
  const [booted, setBooted] = useState(() => safeGetSession('eh_booted') === '1');
  // Auto-skip boot for any route that isn't the marketing home, OR when the URL has query params (email deep-links always carry ?pay=1, ?id=, etc.)
  useEffect(() => {
    if (booted) return;
    const isHome = location.pathname === '/' || location.pathname === '';
    const hasQuery = !!location.search && location.search.length > 1;
    if (!isHome || hasQuery) {
      safeSetSession('eh_booted', '1');
      setBooted(true);
    }
  }, [location.pathname, location.search, booted]);
  if (booted) return null;
  return <BootScreen onDone={() => { safeSetSession('eh_booted', '1'); setBooted(true); }} />;
};

function App() {
  return (
    <ThemeProvider>
      <SiteConfigProvider>
        <AuthProvider>
          <CartProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <Analytics />
              <BootGate />
              <Routes>
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/fonts" element={<FontPreview />} />
                <Route path="/login" element={<SiteShell><Login /></SiteShell>} />
                <Route path="/signup" element={<SiteShell><Signup /></SiteShell>} />
                <Route path="/me" element={<SiteShell><MyAccount /></SiteShell>} />
                <Route path="/me/wallet" element={<SiteShell><MyWallet /></SiteShell>} />
                <Route path="/receipt/:id" element={<SiteShell><Receipt /></SiteShell>} />
                <Route path="/refund/:id" element={<SiteShell><RefundTracker /></SiteShell>} />
                <Route path="/payments/return" element={<SiteShell><PaymentReturn /></SiteShell>} />
                <Route path="/me/spin" element={<SiteShell><SpinWheel /></SiteShell>} />
                <Route path="/me/orders/:id" element={<SiteShell><OrderDetail /></SiteShell>} />
                <Route path="/me/referrals" element={<SiteShell><ReferralsPage /></SiteShell>} />
                <Route path="/cart" element={<SiteShell><CartPage /></SiteShell>} />
                <Route path="/hacking-servers" element={<SiteShell><HackingInfraPage /></SiteShell>} />
                <Route path="/hacking-infrastructure" element={<SiteShell><HackingInfraPage /></SiteShell>} />
                <Route path="/subscribe" element={<SiteShell><Subscribe /></SiteShell>} />
                <Route path="/feed" element={<SiteShell><FeedPage /></SiteShell>} />
                <Route path="/feed/p/:postId" element={<SiteShell><FeedPage /></SiteShell>} />
                <Route path="/feed/r/:reelId" element={<SiteShell><FeedPage /></SiteShell>} />
                <Route path="/" element={<SiteShell><Home /></SiteShell>} />
                <Route path="/books" element={<SiteShell><BooksPage /></SiteShell>} />
                <Route path="/memberships" element={<SiteShell><MembershipsPage /></SiteShell>} />
                <Route path="/recovery" element={<SiteShell><AdvisoryPage /></SiteShell>} />
                <Route path="/advisory" element={<SiteShell><AdvisoryPage /></SiteShell>} />
                <Route path="/consultation" element={<SiteShell><AdvisoryPage /></SiteShell>} />
                <Route path="/consult" element={<SiteShell><AdvisoryPage /></SiteShell>} />
                <Route path="/blogs" element={<SiteShell><AdvisoryPage /></SiteShell>} />
                <Route path="/blog" element={<SiteShell><AdvisoryPage /></SiteShell>} />
                <Route path="/tools" element={<SiteShell><ToolsPage /></SiteShell>} />
                <Route path="/tools/diagnose" element={<SiteShell><ToolDiagnose /></SiteShell>} />
                <Route path="/tools/appeal" element={<SiteShell><ToolAppeal /></SiteShell>} />
                <Route path="/tools/security-score" element={<SiteShell><ToolSecurityScore /></SiteShell>} />
                <Route path="/tools/breach" element={<SiteShell><BreachChecker /></SiteShell>} />
                <Route path="/tools/odds" element={<SiteShell><RecoveryOdds /></SiteShell>} />
                <Route path="/tools/phishing" element={<SiteShell><PhishingDetector /></SiteShell>} />
                <Route path="/tools/account-worth" element={<SiteShell><AccountWorth /></SiteShell>} />
                <Route path="/tools/selfie-coach" element={<SiteShell><SelfieCoach /></SiteShell>} />
                <Route path="/tools/security" element={<SiteShell><SecuritySuitePage /></SiteShell>} />
                <Route path="/tools/security-suite" element={<SiteShell><SecuritySuitePage /></SiteShell>} />
                <Route path="/track" element={<SiteShell><OrderTracker /></SiteShell>} />
                <Route path="/faq" element={<SiteShell><FAQPage /></SiteShell>} />
                <Route path="/info" element={<SiteShell><InfoPage /></SiteShell>} />
                <Route path="/info/team" element={<SiteShell><TeamPage /></SiteShell>} />
                <Route path="/team" element={<SiteShell><TeamPage /></SiteShell>} />
                <Route path="/about" element={<SiteShell><AboutUsPage /></SiteShell>} />
                <Route path="/about-us" element={<SiteShell><AboutUsPage /></SiteShell>} />
                <Route path="/privacy" element={<SiteShell><PrivacyPolicyPage /></SiteShell>} />
                <Route path="/privacy-policy" element={<SiteShell><PrivacyPolicyPage /></SiteShell>} />
                <Route path="/terms" element={<SiteShell><TermsOfUsePage /></SiteShell>} />
                <Route path="/terms-of-use" element={<SiteShell><TermsOfUsePage /></SiteShell>} />
                <Route path="/ethics" element={<SiteShell><EthicsPage /></SiteShell>} />
                <Route path="/ethical-charter" element={<SiteShell><EthicsPage /></SiteShell>} />
                <Route path="*" element={<SiteShell><NotFound /></SiteShell>} />
              </Routes>
            </BrowserRouter>
            <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: '#0d1115', color: '#d6e2dc', border: '1px solid #1a2128' } }} />
          </div>
          </CartProvider>
        </AuthProvider>
      </SiteConfigProvider>
    </ThemeProvider>
  );
}
export default App;
